"use client";

import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useRouter } from "@/lib/i18n/navigation";
import { useLocale, useTranslations } from "next-intl";
import { CITIES } from "@/lib/data/specialties";
import { localizedName } from "@/lib/data/i18n-helpers";

const POPULAR = [
  "casablanca",
  "rabat",
  "marrakech",
  "fes",
  "tanger",
  "agadir",
  "meknes",
  "oujda",
  "kenitra",
  "tetouan",
];

function normalize(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
}

type Props = {
  currentCitySlug?: string;
  specialtyLabel?: string;
  baseParams: Record<string, string>;
  /**
   * Quand true, le picker s'ouvre automatiquement au mount.
   * Activé par /recherche?pickCity=1 — utilisé après que l'utilisateur a saisi
   * un médecin/spécialité dans le compact searchbar mobile, pour le forcer à
   * choisir une ville à l'étape suivante.
   */
  autoOpen?: boolean;
};

export function CityPickerMobile({ currentCitySlug, specialtyLabel, baseParams, autoOpen = false }: Props) {
  const router = useRouter();
  const t = useTranslations("search");
  const locale = useLocale();
  const [open, setOpen] = useState(autoOpen);
  const [q, setQ] = useState("");
  const [locating, setLocating] = useState(false);

  const currentCity = useMemo(
    () => (currentCitySlug ? CITIES.find((c) => c.slug === currentCitySlug) : undefined),
    [currentCitySlug],
  );

  const matches = useMemo(() => {
    const n = normalize(q);
    if (!n) {
      return CITIES.filter((c) => POPULAR.includes(c.slug)).sort(
        (a, b) => POPULAR.indexOf(a.slug) - POPULAR.indexOf(b.slug),
      );
    }
    return CITIES.filter(
      (c) =>
        normalize(c.nameFr).includes(n) ||
        normalize(c.nameAr ?? "").includes(n) ||
        normalize(c.nameEn ?? "").includes(n) ||
        normalize(c.slug).includes(n),
    ).slice(0, 30);
  }, [q]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
      setQ("");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [open]);

  function pickCity(slug: string | null) {
    const qs: Record<string, string> = {};
    for (const [k, v] of Object.entries(baseParams)) {
      // pickCity = flag éphémère pour autoOpen, on le drop après sélection
      if (k === "city" || k === "ville" || k === "pickCity" || v === undefined) continue;
      qs[k] = String(v);
    }
    if (slug) qs.ville = slug;
    const s = new URLSearchParams(qs).toString();
    router.push(`/recherche${s ? `?${s}` : ""}` as never);
    setOpen(false);
  }

  function locate() {
    if (locating) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        let best: { slug: string; dist: number } | null = null;
        for (const c of CITIES) {
          const dx = c.lat - pos.coords.latitude;
          const dy = c.lng - pos.coords.longitude;
          const d = dx * dx + dy * dy;
          if (!best || d < best.dist) best = { slug: c.slug, dist: d };
        }
        setLocating(false);
        if (best) pickCity(best.slug);
      },
      () => setLocating(false),
      { timeout: 6000 },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          aria-label={t("cityPicker.chooseCity")}
          className="inline-flex w-full items-center gap-2 rounded-full bg-white/95 px-4 py-2.5 text-start text-[14px] font-semibold text-[var(--sahha-primary-800)] shadow-sm transition-all active:scale-[0.99]"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-[var(--sahha-primary-700)]" fill="none" aria-hidden>
            <path d="M12 22s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 12 12Z" stroke="currentColor" strokeWidth="2" />
            <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span className="flex-1 truncate">
            {currentCity ? localizedName(currentCity, locale) : t("cityPicker.allMoroccoCities")}
          </span>
          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-[var(--sahha-text-400)]" fill="none" aria-hidden>
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[80] bg-[rgb(15_30_58_/_0.5)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-[90] flex flex-col overflow-hidden bg-[var(--sahha-surface-1)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right data-[state=open]:duration-250 focus:outline-none"
        >
          <div className="bg-[var(--sahha-primary-700)] pt-[max(env(safe-area-inset-top),0.5rem)] pb-3">
            <div className="flex items-center gap-2 px-3 pt-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label={t("cityPicker.backAria")}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 text-white"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </Dialog.Close>
              <Dialog.Title className="flex-1 text-center text-[17px] font-bold text-white">
                {t("cityPicker.headerWhere")}
              </Dialog.Title>
              <span aria-hidden className="w-10 shrink-0" />
            </div>
            {specialtyLabel && (
              <div className="mt-1 ml-4 inline-flex items-center gap-1.5 self-start rounded-full bg-white/20 px-2.5 py-1 text-[12px] font-bold text-white backdrop-blur">
                <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" aria-hidden>
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                  <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>{specialtyLabel}</span>
              </div>
            )}
          </div>

          <div className="px-5 pt-5">
            <p className="mb-2 text-[12px] font-bold uppercase tracking-wider text-[var(--sahha-text-700)]">
              {t("cityPicker.addressLabel")}
            </p>
            <div className="flex items-center gap-2.5 rounded-2xl border-2 border-[var(--sahha-primary-600)] bg-white px-3.5 py-3">
              <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-[var(--sahha-text-500)]" fill="none" aria-hidden>
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("cityPicker.placeholder")}
                className="flex-1 bg-transparent text-[15px] text-[var(--sahha-text-900)] placeholder:text-[var(--sahha-text-400)] focus:outline-none"
                autoFocus
              />
              {q.length > 0 && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  aria-label={t("cityPicker.clearAria")}
                  className="text-[var(--sahha-text-500)] hover:text-[var(--sahha-text-700)]"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
                    <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={locate}
              disabled={locating}
              className="mt-2 flex w-full items-center gap-2 px-1 py-3 text-start text-[15px] font-bold text-[var(--sahha-primary-700)] disabled:opacity-50"
            >
              {locating ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[var(--sahha-primary-700)] border-t-transparent" />
              ) : (
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                  <path d="M3 11l18-7-7 18-2-8-9-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                </svg>
              )}
              {t("cityPicker.aroundMe")}
            </button>

            <button
              type="button"
              onClick={() => pickCity(null)}
              className="block w-full px-1 py-3 text-start text-[14px] font-semibold text-[var(--sahha-text-700)]"
            >
              {t("cityPicker.searchAllMorocco")}
            </button>
          </div>

          <p className="mt-4 mb-2 px-6 text-[11px] font-bold uppercase tracking-wider text-[var(--sahha-text-600)]">
            {q ? t("cityPicker.suggestions") : t("cityPicker.popularCities")}
          </p>

          <div className="flex-1 overflow-y-auto px-3 pb-6">
            {matches.length === 0 ? (
              <p className="mt-10 text-center text-[14px] text-[var(--sahha-text-600)]">
                {t("cityPicker.noMatch")}
              </p>
            ) : (
              <ul>
                {matches.map((c) => (
                  <li key={c.slug}>
                    <button
                      type="button"
                      onClick={() => pickCity(c.slug)}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-start transition-colors active:bg-white"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-50)]">
                        <svg viewBox="0 0 24 24" className="h-4 w-4 text-[var(--sahha-primary-700)]" fill="none" aria-hidden>
                          <path d="M12 22s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z" stroke="currentColor" strokeWidth="2.2" />
                          <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="2.2" />
                        </svg>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-semibold text-[var(--sahha-text-900)]">
                          {localizedName(c, locale)}
                        </span>
                        <span className="mt-0.5 block truncate text-[12px] text-[var(--sahha-text-600)]">
                          {c.region}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
